import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import {
    ArrowRightOutlined,
    CheckOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    DeleteOutlined,
    InfoCircleFilled,
    MessageFilled,
    WarningFilled,
} from "@ant-design/icons";

import "./NotificationItem.scss";

/**
 * One notification, rendered identically in the notifications page and in
 * the navbar drawer.
 *
 * It exists because those two surfaces used to each carry their own copy of
 * this card - their own colour constants, their own inline SVG icon set,
 * their own runtime-injected stylesheet - and the copies had already
 * drifted apart (different paddings, different type sizes, different
 * hover treatments) for what is the same object showing the same fields.
 * One component means the drawer and the page can no longer disagree about
 * what a notification looks like.
 *
 * Severity is expressed through the icon's shape as well as its colour, so
 * the level survives for a reader who cannot separate the hues.
 */

export type NotificationSeverityKey =
    | "success"
    | "info"
    | "warning"
    | "error"
    | "other";

const SEVERITY_LABEL: Record<NotificationSeverityKey, string> = {
    success: "Success",
    info: "Info",
    warning: "Warning",
    error: "Error",
    other: "Note",
};

const SEVERITY_ICON: Record<NotificationSeverityKey, React.ReactNode> = {
    success: <CheckCircleFilled />,
    info: <InfoCircleFilled />,
    warning: <WarningFilled />,
    error: <CloseCircleFilled />,
    other: <MessageFilled />,
};

/** Narrows whatever the API sent to a key this component actually styles. */
export function severityKey(raw?: string | null): NotificationSeverityKey {
    const key = (raw || "other").toLowerCase();
    return key === "success" ||
        key === "info" ||
        key === "warning" ||
        key === "error"
        ? key
        : "other";
}

/**
 * Only high and medium produce a chip. Every notification carries a
 * priority, so rendering "LOW" on the majority of cards added a badge to
 * every row that told the reader nothing and made the two that matter
 * harder to spot.
 */
type PriorityKey = "high" | "medium" | null;

function priorityKey(raw?: string | null): PriorityKey {
    const key = (raw || "").toLowerCase();
    if (key === "high") return "high";
    if (key === "medium") return "medium";
    return null;
}

const PRIORITY_LABEL: Record<"high" | "medium", string> = {
    high: "High priority",
    medium: "Medium priority",
};

export interface NotificationItemProps {
    notification: any;
    /** Relative timestamp, formatted by the caller (the two surfaces use different helpers). */
    time: string;
    /** `compact` is the drawer's narrower column; `full` is the page. */
    variant?: "full" | "compact";
    onMarkRead: (id: string) => void;
    onDelete: (id: string) => void;
    /** Called after a link inside the card is followed (the drawer closes itself). */
    onNavigate?: () => void;
    /** Where "Open" points when the notification carries no link of its own. */
    fallbackLink?: string;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
    notification: n,
    time,
    variant = "full",
    onMarkRead,
    onDelete,
    onNavigate,
    fallbackLink,
}) => {
    const sev = severityKey(n.severity);
    const priority = priorityKey(n.priority);
    const unread = !n.read;
    const newUser = n.data?.newUser;

    const className = [
        "notif-item",
        `notif-item--${sev}`,
        unread ? "notif-item--unread" : "",
        variant === "compact" ? "notif-item--compact" : "",
    ]
        .filter(Boolean)
        .join(" ");

    const link = n.link || fallbackLink;

    return (
        <article className={className}>
            <span className="notif-item__icon" aria-hidden="true">
                {SEVERITY_ICON[sev]}
            </span>

            <div className="notif-item__main">
                <div className="notif-item__meta">
                    <span className="notif-item__sev">
                        {SEVERITY_LABEL[sev]}
                    </span>
                    {n.category && (
                        <span className="notif-item__category">
                            {n.category}
                        </span>
                    )}
                    {priority && (
                        <span
                            className={`notif-item__priority notif-item__priority--${priority}`}
                        >
                            {PRIORITY_LABEL[priority]}
                        </span>
                    )}
                    <span className="notif-item__time">{time}</span>
                </div>

                {/* The unread state is announced in the title rather than
                    carried only by the card's tint, which a screen reader
                    never sees. */}
                <div className="notif-item__title">
                    {unread && <span className="notif-sr-only">Unread. </span>}
                    {n.title}
                </div>

                {n.message && (
                    <div className="notif-item__message">{n.message}</div>
                )}

                {newUser && (
                    <div className="notif-item__user">
                        <span className="notif-item__user-name">
                            {newUser.name}
                        </span>
                        {newUser.jobTitle && (
                            <span className="notif-item__user-tag">
                                {newUser.jobTitle}
                            </span>
                        )}
                        {newUser.role && (
                            <span className="notif-item__user-role">
                                {newUser.role}
                            </span>
                        )}
                    </div>
                )}

                <div className="notif-item__actions">
                    {link && (
                        <Link
                            to={link}
                            className="notif-item__btn notif-item__btn--primary"
                            onClick={onNavigate}
                        >
                            {n.link ? "View" : "Open"}
                            <ArrowRightOutlined />
                        </Link>
                    )}

                    {unread && (
                        <button
                            type="button"
                            className="notif-item__btn notif-item__btn--ghost"
                            onClick={() => onMarkRead(n._id)}
                        >
                            <CheckOutlined />
                            Mark read
                        </button>
                    )}

                    <Tooltip title="Delete notification">
                        <button
                            type="button"
                            className="notif-item__btn notif-item__btn--danger"
                            onClick={() => onDelete(n._id)}
                            aria-label={`Delete notification: ${n.title || "untitled"}`}
                        >
                            <DeleteOutlined />
                            {variant === "full" && "Delete"}
                        </button>
                    </Tooltip>
                </div>
            </div>
        </article>
    );
};

export default NotificationItem;
